import { createClient } from '@supabase/supabase-js';
import { SupabaseConfigurationError } from './errors';

export interface ForqSupabaseConfig {
  readonly url: string;
  readonly anonKey: string;
}

export function isSupabaseConfigured(
  config: Partial<ForqSupabaseConfig>,
): config is ForqSupabaseConfig {
  return Boolean(config.url?.trim() && config.anonKey?.trim());
}

/**
 * Der FORQ-Client. Traegt `forq` als Standardschema im Typ -- deshalb abgeleitet statt annotiert:
 * die Generics von SupabaseClient sind verschachtelt, und eine handgeschriebene Kopie waere beim
 * naechsten Versionsupdate still falsch.
 */
export type ForqSupabaseClient = ReturnType<typeof createForqSupabaseClient>;

export function createForqSupabaseClient(config: ForqSupabaseConfig) {
  const url = config.url.trim();
  const anonKey = config.anonKey.trim();

  if (!url || !anonKey) {
    throw new SupabaseConfigurationError('Supabase URL and anonymous key are required.');
  }

  try {
    const parsedUrl = new URL(url);
    if (!['http:', 'https:'].includes(parsedUrl.protocol)) {
      throw new Error('Unsupported protocol.');
    }
  } catch {
    throw new SupabaseConfigurationError('Supabase URL must be a valid HTTP or HTTPS URL.');
  }

  return createClient(url, anonKey, {
    // Alle 119 RPC-Funktionen von FORQ liegen im Schema `forq`, nicht in `public` -- die
    // Tabellen fasst der Client nie direkt an, jeder Zugriff laeuft ueber eine security-definer
    // Funktion. Ohne diese Zeile fragt supabase-js `public` nach Funktionen, die es dort nicht
    // gibt: "Could not find the function public.get_my_tenants in the schema cache".
    // serviceBilling ueberschreibt das je Aufruf mit .schema('forq_next_preview').
    //
    // Setzt voraus, dass `forq` in Supabase unter Project Settings -> API -> Exposed schemas
    // freigegeben ist (lokal: supabase/config.toml, Schluessel `schemas`).
    db: { schema: 'forq' },
    auth: {
      autoRefreshToken: true,
      detectSessionInUrl: true,
      persistSession: true,
    },
  });
}
